import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useDeferredValue,
} from 'react';
import { JsonForms } from '@jsonforms/react';
import { materialRenderers, materialCells } from '@jsonforms/material-renderers';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getApiEndpoint } from '../config/apiConfig';
import { isDevelopment } from '../utils/environment';
import { fetchJson, formatError } from '../utils/api';
import { diffPaths } from '../utils/diff';

type JsonSchema = Record<string, unknown>;
type JsonData = Record<string, unknown> | unknown[];

interface FormTabProps {
  name: string;
  description: string;
  idToken?: string;
  isActive: boolean;
  saveSignal: number;
  onDirtyChange: (name: string, isDirty: boolean) => void;
  onValidationChange: (name: string, hasErrors: boolean) => void;
  onErrorChange: (name: string, errorMessage: string | null) => void;
  onPendingChange: (name: string, paths: string[]) => void;
  onSavingChange: (name: string, isSaving: boolean) => void;
}

const buildSchemaEndpoint = (name: string) => `/branding/app/schema/${name}`;
const buildDataEndpoint = (name: string) => `/branding/app/payload/${name}`;

export const FormTab: React.FC<FormTabProps> = ({
  name,
  description,
  idToken,
  isActive,
  saveSignal,
  onDirtyChange,
  onValidationChange,
  onErrorChange,
  onPendingChange,
  onSavingChange,
}) => {
  const isDevMode = isDevelopment();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<JsonData | null>(null);
  const [lastSavedData, setLastSavedData] = useState<JsonData | null>(null);
  const [formErrors, setFormErrors] = useState<unknown[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [pendingPaths, setPendingPaths] = useState<string[]>([]);
  const lastHandledSaveSignalRef = useRef(0);
  const schemaQuery = useQuery({
    queryKey: ['branding-schema', name],
    enabled: Boolean(idToken),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
    retry: (failureCount, error: any) => {
        console.error(error);
      // don't retry on client side errors
      if (error?.status >= 400 && error?.status <= 499) {
        return false;
      }
      return failureCount < 3;
    },
    queryFn: () =>
      fetchJson<JsonSchema>(getApiEndpoint(buildSchemaEndpoint(name), isDevMode), {
        token: idToken,
      }),
  });

  const dataQuery = useQuery({
    queryKey: ['branding-data', name],
    enabled: Boolean(idToken),
    refetchOnWindowFocus: false,
    queryFn: () =>
      fetchJson<JsonData>(getApiEndpoint(buildDataEndpoint(name), isDevMode), {
        token: idToken,
      }),
  });

  const saveMutation = useMutation({
    mutationFn: (payload: JsonData) =>
      fetchJson<JsonData>(getApiEndpoint(buildDataEndpoint(name), isDevMode), {
        method: 'POST',
        body: JSON.stringify(payload),
        token: idToken,
      }),
    onSuccess: (data: JsonData) => {
      queryClient.setQueryData(['branding-data', name], data);
      setFormData(data);
      setLastSavedData(data);
      setIsDirty(false);
    },
  });

  const handleUpdates = useCallback((errors: unknown[], data: JsonData) => {
    setFormErrors(errors ?? []);
    setFormData(data);
  }, []);

  useEffect(() => {
    if (dataQuery.data !== undefined && !isDirty) {
      setFormData(dataQuery.data);
      setLastSavedData(dataQuery.data);
    }
  }, [dataQuery.data, isDirty]);

  useEffect(() => {
    if (formData === null || lastSavedData === null) {
      setPendingPaths([]);
      setIsDirty(false);
      return;
    }
    const pending = diffPaths(lastSavedData, formData);
    setPendingPaths(pending);
    setIsDirty(pending.length > 0);
  }, [formData, lastSavedData]);

  useEffect(() => {
    onDirtyChange(name, isDirty);
  }, [name, isDirty, onDirtyChange]);

  useEffect(() => {
    onValidationChange(name, formErrors.length > 0);
  }, [name, formErrors.length, onValidationChange]);

  useEffect(() => {
    onPendingChange(name, pendingPaths);
  }, [name, pendingPaths, onPendingChange]);

  useEffect(() => {
    onSavingChange(name, saveMutation.isPending);
  }, [name, saveMutation.isPending, onSavingChange]);

  const errorMessage = useMemo(() => {
    const errors = [schemaQuery?.error, dataQuery?.error, saveMutation?.error].filter(Boolean);
    if (!errors || errors?.length === 0) {
      return null;
    }
    return errors.map(formatError).join(' | ');
  }, [schemaQuery.error, dataQuery.error, saveMutation.error]);

  useEffect(() => {
    onErrorChange(name, errorMessage);
  }, [name, errorMessage, onErrorChange]);

  useEffect(() => {
    if (saveSignal <= lastHandledSaveSignalRef.current) {
      return;
    }
    lastHandledSaveSignalRef.current = saveSignal;

    if (isDirty && formData !== null) {
      saveMutation.mutate(formData);
    }
  }, [saveSignal, isDirty, formData, saveMutation]);

  const renderData = formData ?? dataQuery.data ?? null;

  const deferredSchema = useDeferredValue(schemaQuery.data);
  const deferredData = useDeferredValue(renderData);

  // Only render the heavy form when this tab is active
  if (!isActive) {
    return null;
  }

  const isLoading = schemaQuery.isLoading || dataQuery.isLoading;
  const isFormReady = Boolean(schemaQuery.data) && dataQuery.isSuccess;

  return (
    <div className="tab-panel">
      <h3>{description}</h3>
      <div className="form-card">
        {(isLoading || !isFormReady) &&(
          <div className="loading-placeholder">
            <div className="loading-title" />
            <div className="loading-line" />
            <div className="loading-line" />
            <div className="loading-line short" />
          </div>
        )}

        {(!isLoading && isFormReady) && (
          <JsonForms
            schema={deferredSchema}
            data={deferredData}
            renderers={materialRenderers}
            cells={materialCells}
            onChange={({ errors, data }: { errors?: unknown[]; data: JsonData }) =>
              handleUpdates(errors ?? [], data)
            }
          />
        )}
      </div>
    </div>
  );
};

